import { Button as FuiButton } from "@fluentui/react-components";
import {
  CheckmarkCircle20Regular,
  Eye16Regular,
  EyeOff16Regular,
  LinkSquare20Filled
} from "@fluentui/react-icons";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { useState } from "react";
import { useParams } from "react-router";

import { getTabToken } from "common/MetaActions";
import { usePermission } from "hooks/use-permissions";
import { downloadAndSaveFile } from "common/downloadFile";
import { splitName } from "common/splitName/splitName";
import { Icon } from "components/icon/Icon";
import Modal from "components/modal/Modal";
import ModalFooter from "components/modal/ModalFooter";
import Persona from "components/persona/Persona";
import { Tooltip } from "components/tooltip/Tooltip";
import config from "config/config";
import { buildThreadLink } from "helpers/inbox/messageQueryParam";
import { useRequiredCustomerId } from "hooks/use-customer-id";
import { useSelectedMessageId } from "hooks/use-selected-message-id";
import { Attachment } from "models/dashboard/dashboard";
import { MessageItem, Thread } from "models/inbox/inbox";
import { useSanitizedHTML } from "hooks/use-sanitized-html";
import { useRootStore } from "store";

import "../../index.scss";
import "./Message.scss";

type Props = {
  id: string;
  message: MessageItem;
  thread?: Thread;
};

export default function Message({ id, message, thread }: Props) {
  const customerId = useRequiredCustomerId();
  const isInternalUser = usePermission("isInternal");
  const { messageId, isHidden } = message;
  const [showUnhideConfirm, setShowUnhideConfirm] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [tooltipText, setTooltipText] = useState("Kopiera länk");

  const { inboxType } = useParams<{
    inboxType?: string;
  }>();

  const shouldShowHideBtn = inboxType !== "sent" && isInternalUser;
  const isSentBox = inboxType === "sent";

  const selectedMessageId = useSelectedMessageId();
  const { currentThread, hideThreadMessage, unhideThreadMessage } =
    useRootStore((state) => state.inbox);
  const { addNotification } = useRootStore((state) => state.toastNotifications);

  const isActive =
    messageId === selectedMessageId &&
    currentThread?.threadId === thread?.threadId;

  const sender = splitName(message.formatedFrom);
  const timestamp = format(new Date(message.createdAt), "HH:mm");
  const date = format(new Date(message.createdAt), "d LLLL yyyy", {
    locale: sv
  });

  const markup = useSanitizedHTML(message.body);

  const handleHideBtnClick = () => {
    if (isHidden) {
      setShowUnhideConfirm(true);
    } else {
      hideThreadMessage(customerId, messageId, thread?.threadId || "");
    }
  };

  const handleConfirmUnhide = () => {
    unhideThreadMessage(customerId, messageId, thread?.threadId || "");
    setShowUnhideConfirm(false);
  };

  const handleCancelUnhide = () => {
    setShowUnhideConfirm(false);
  };

  const buildMessageShareUrl = (): string => {
    const threadId = thread?.threadId || "";
    const baseUrl = window.location.origin;
    const relativePath = buildThreadLink(
      inboxType || "my",
      threadId,
      customerId,
      messageId
    );
    return `${baseUrl}${relativePath}`;
  };

  const handleCopyMessageLink = () => {
    const shareUrl = buildMessageShareUrl();

    navigator.clipboard.writeText(shareUrl).then(
      () => {
        setIsCopied(true);
        setTooltipText("Kopierad!");
        addNotification("success", "Länken kopierad!");

        setTimeout(() => {
          setIsCopied(false);
          setTooltipText("Kopiera länk");
        }, 2000);
      },
      () => {
        addNotification("error", "Misslyckades att kopiera länken.");
      }
    );
  };

  const handleOpenDocument = (item: { id: string; name: string }) => {
    const url = config.api.archive.download(customerId, item.id);
    const headers = new Headers();
    headers.append("Authorization", `Bearer ${getTabToken()}`);
    downloadAndSaveFile(url, item.name, headers);
  };

  const handleOnClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    attachment: Attachment
  ) => {
    e.preventDefault();
    handleOpenDocument(attachment);
  };

  return (
    <>
      {showUnhideConfirm && (
        <Modal
          header={<h3 className="m-0">Visa meddelande</h3>}
          onDismiss={() => setShowUnhideConfirm(false)}
          modalTitle="show or hide a message"
          testId="messages-modal"
          size="xs"
          footer={
            <ModalFooter
              onCancel={handleCancelUnhide}
              onConfirm={handleConfirmUnhide}
              confirmText="Ja, visa meddelande"
              justifyActions="end"
              actionsDefaultSize="sm"
            />
          }
        >
          <div className="hide-modal-text">
            <p>Vill du visa detta meddelande för kunden?</p>
            <div className="hide-modal-message-content">
              {thread !== undefined && (
                <div>
                  <span className="sender">
                    {sender.firstName} {sender.lastName}
                  </span>
                </div>
              )}

              <div
                className="html-editor fw-normal mb-1"
                dangerouslySetInnerHTML={markup}
              />
            </div>
          </div>
        </Modal>
      )}
      <div
        className={`thread-preview-msg w-100 ${isSentBox ? "sent-message" : ""} ${isHidden && shouldShowHideBtn ? "hidden-message" : ""} ${isActive ? "active-message" : ""}`}
        id={id}
      >
        <div className="thread-preview-header d-flex justify-content-between">
          <div>
            <div className="d-flex align-items-center">
              <Persona
                firstName={sender.firstName}
                lastName={sender.lastName}
                className="mr-3 message-person"
              />

              <div className="preview-message-sender fw-normal mb-2">
                Från: {sender.firstName} {sender.lastName}
              </div>

              {/* Copy message link button */}
              {!isSentBox && (
              <Tooltip content={<div>{tooltipText}</div>}>
                <FuiButton
                  size="small"
                  appearance="subtle"
                  className="copy-message-link-btn ml-2"
                  icon={
                    isCopied ? (
                      <CheckmarkCircle20Regular />
                    ) : (
                      <LinkSquare20Filled />
                    )
                  }
                  onClick={handleCopyMessageLink}
                  aria-label={tooltipText}
                />
              </Tooltip>
              )}
            </div>
          </div>

          <div className="d-flex">
            <span className="text-right">
              <time className="thread-preview-date">{date}</time>
              <time className="thread-preview-date">{timestamp}</time>
            </span>

            {/* Only Internal users can hide/unhide a message */}
            {shouldShowHideBtn && (
              <Tooltip
                className="ml-2"
                content={
                  <div>{isHidden ? `Visa för kund` : `Dölj för kund`}</div>
                }
              >
                <FuiButton
                  size="small"
                  appearance="subtle"
                  className={`visibility-btn ${isHidden ? "hidden" : "visible"}`}
                  icon={isHidden ? <EyeOff16Regular /> : <Eye16Regular />}
                  onClick={handleHideBtnClick}
                />
              </Tooltip>
            )}
          </div>
        </div>

        <div
          className="html-editor fw-normal mb-1"
          dangerouslySetInnerHTML={markup}
        />

        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-4">
            {message.attachments.map((attachment) => {
              return (
                <div
                  key={attachment.id}
                  className="attachment"
                  onClick={(e) => handleOnClick(e, attachment)}
                >
                  {/* NOTE: REPLACE WITH DOWNLOAD ICON */}
                  <Icon name="paperclip" className="mr-2" />
                  <span className="attachment-name">{attachment.name}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
